import { addDoc, collection, deleteDoc, doc, onSnapshot, setDoc, Timestamp, updateDoc } from "firebase/firestore";
import { createContext, FC, useContext, useEffect, useState } from "react";
import { useFirestore, useSigninCheck } from "./use-firebase";
import { toast } from "sonner";
import { CollectionsEnum } from "../lib/enums";
import { Listing, Sale, InventoryItem } from "../lib/types";
import { useInventoryContext } from "./use-inventory";

type ListingContextType = {
  listings: Listing[]
  sales: Sale[]
  addListing: (listing: Listing) => Promise<void>
  deleteListing: (listingId: string) => Promise<void>
  updateListing: (listing: Listing) => Promise<void>
  setListing: (listing: Listing) => Promise<void>
  addSale: (sale: Sale, inventoryItem: InventoryItem) => Promise<void>
  undoSale: (sale: Sale, inventoryItem: InventoryItem) => Promise<void>
  getSalesForListing: (listingId: string) => Sale[]
  getTotalSalesRevenue: () => number
  getActiveListingsCount: () => number
  getSoldListingsCount: () => number
}

const ListingContext = createContext<ListingContextType>({
  listings: [],
  sales: [],
  addListing: async () => { },
  deleteListing: async () => { },
  updateListing: async () => { },
  setListing: async () => { },
  addSale: async () => { },
  undoSale: async () => { },
  getSalesForListing: () => [],
  getTotalSalesRevenue: () => 0,
  getActiveListingsCount: () => 0,
  getSoldListingsCount: () => 0
});

// A custom hook for global state management
export const useListingContext = () => {
  const ic = useContext(ListingContext);
  if (!ic) {
    throw new Error("useListingContext must be used within a ListingContextProvider");
  }
  return ic;
};

type ListingContextProviderProps = {
  children: React.ReactNode;
}

// The provider for your state
export const ListingContextProvider: FC<ListingContextProviderProps> = ({ children }) => {
  const db = useFirestore()
  const { data: signInData } = useSigninCheck()
  const { inventory, updateInventoryElem } = useInventoryContext()

  const [listings, setListings] = useState<Listing[]>([])
  const [sales, setSales] = useState<Sale[]>([])

  //TODO: Only include the Listings data if the user has a subscription for it

  //LISTING SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/listings`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Listings Found")
          setListings([])
          return
        } else {
          const listingObs: Listing[] = []

          for (let i = 0; i < snapshot.docs.length; i++) {
            const listing = snapshot.docs[i]
            const data = listing.data()

            // Migration helper: convert old format to new format
            const migratedListing = {
              ...data,
              id: listing.id,
              // Handle old 'sold' boolean field
              status: data.status || (data.sold ? 'sold' : 'active'),
              // Handle old 'quantity' field
              quantityListed: data.quantityListed,
              quantitySold: data.quantitySold,
              quantityAvailable: data.quantityAvailable
            } as Listing

            listingObs.push(migratedListing)
          }

          setListings(listingObs.sort((a, b) => {
            return a.createdAt > b.createdAt ? -1 : 1
          }))
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setListings([])
    }
  }, [signInData?.signedIn, db, signInData])

  //SALES SNAPSHOT INITIALIZATION
  useEffect(() => {
    if (signInData?.signedIn) {
      const unsub = onSnapshot(collection(db, `users/${signInData?.user?.uid}/sales`), (snapshot) => {
        if (snapshot.empty) {
          console.warn("No Sales Found")
          setSales([])
          return
        } else {
          const salesObs: Sale[] = []

          for (let i = 0; i < snapshot.docs.length; i++) {
            const sale = snapshot.docs[i]
            salesObs.push({ ...sale.data(), id: sale.id } as Sale)
          }

          setSales(salesObs.sort((a, b) => {
            return a.createdAt > b.createdAt ? -1 : 1
          }))
        }
      })
      return () => unsub();
    } else {
      // Clear state when user logs out
      setSales([])
    }
  }, [signInData?.signedIn, db, signInData])

  const addListing = async (listing: Listing) => {
    try {
      if (listing?.id) {
        await setDoc(doc(db, `users/${signInData?.user?.uid}/${CollectionsEnum.LISTING}/${listing.id}`), {
          ...listing,
          createAt: Timestamp.now(),
        })
        return
      }
      await addDoc(collection(db, `users/${signInData?.user?.uid}/${CollectionsEnum.LISTING}`), {
        ...listing,
        createAt: Timestamp.now(),
      })
    } catch (e) {
      console.error(e)
      toast.error("error adding to " + CollectionsEnum.LISTING)
    }
  }

  const deleteListing = async (listingId: string) => {
    try {
      await deleteDoc(doc(db, `users/${signInData?.user?.uid}/${CollectionsEnum.LISTING}/${listingId}`))
    } catch (e) {
      console.error(e)
      toast.error("error removing listing")
    }
  }

  const updateListing = async (listing: Listing) => {
    try {
      await updateDoc(doc(db, `users/${signInData?.user?.uid}/${CollectionsEnum.LISTING}/${listing.id}`), {
        ...listing,
        updatedAt: Timestamp.now(),
      })
      toast.success("Listing updated successfully")
    } catch (e) {
      console.error(e)
      toast.error("error updating listing in" + CollectionsEnum.LISTING)
    }
  }

  const setListing = async (listing: Listing) => {
    try {
      await setDoc(doc(db, `users/${signInData?.user?.uid}/${CollectionsEnum.LISTING}/${listing.id}`), {
        ...listing,
        updatedAt: Timestamp.now(),
      })
      toast.success("Listing updated successfully")
    } catch (e) {
      console.error(e)
      toast.error("error setting listing in" + CollectionsEnum.LISTING)
    }
  }

  const addSale = async (sale: Sale, inventoryItem: InventoryItem) => {
    try {
      // Add the sale record
      await addDoc(collection(db, `users/${signInData?.user?.uid}/${CollectionsEnum.SALE}`), {
        ...sale,
        createdAt: Timestamp.now(),
      })

      // Update the listing quantities
      const listing = listings.find(l => l.id === sale.listingId)
      if (listing) {
        const newQuantitySold = listing.quantitySold + sale.quantitySold
        const newQuantityAvailable = listing.quantityListed - newQuantitySold

        const listingUpdates: Listing = {
          ...listing,
          quantitySold: newQuantitySold,
          quantityAvailable: newQuantityAvailable,
          status: newQuantityAvailable <= 0 ? 'sold' : 'active',
        }

        if (newQuantityAvailable <= 0) {
          listingUpdates.soldAt = Timestamp.now()
        }

        await updateListing(listingUpdates)
      }

      // Deduct from inventory
      if (inventoryItem) {
        const updatedInventory = {
          ...inventoryItem,
          quantity: inventoryItem.quantity - sale.quantitySold
        }
        await updateInventoryElem(updatedInventory, CollectionsEnum.INVENTORY_ITEM)
      }

      toast.success(`Sale recorded! ${sale.quantitySold} unit(s) sold for $${sale.totalAmount.toFixed(2)}`)
    } catch (e) {
      console.error(e)
      toast.error("Error recording sale")
    }
  }

  const undoSale = async (sale: Sale, inventoryItem: InventoryItem) => {
    try {
      // Delete the sale record
      await deleteDoc(doc(db, `users/${signInData?.user?.uid}/${CollectionsEnum.SALE}/${sale.id}`))

      // Update the listing quantities (restore them)
      const listing = listings.find(l => l.id === sale.listingId)
      if (listing) {
        const newQuantitySold = listing.quantitySold - sale.quantitySold
        const newQuantityAvailable = listing.quantityListed - newQuantitySold

        await updateListing({
          ...listing,
          quantitySold: newQuantitySold,
          quantityAvailable: newQuantityAvailable,
          status: newQuantityAvailable > 0 ? 'active' : 'sold',
          soldAt: newQuantityAvailable > 0 ? undefined : listing.soldAt
        })
      }

      // Restore to inventory
      if (inventoryItem) {
        const updatedInventory = {
          ...inventoryItem,
          quantity: inventoryItem.quantity + sale.quantitySold
        }
        await updateInventoryElem(updatedInventory, CollectionsEnum.INVENTORY_ITEM)
      }

      toast.success(`Sale undone! ${sale.quantitySold} unit(s) restored to inventory`)
    } catch (e) {
      console.error(e)
      toast.error("Error undoing sale")
    }
  }

  const getSalesForListing = (listingId: string): Sale[] => {
    return sales.filter(sale => sale.listingId === listingId)
  }

  const getTotalSalesRevenue = (): number => {
    return sales.reduce((total, sale) => total + sale.totalAmount, 0)
  }

  const getActiveListingsCount = (): number => {
    return listings.filter(l => l.status === 'active').length
  }

  const getSoldListingsCount = (): number => {
    return listings.filter(l => l.status === 'sold').length
  }

  return (
    <ListingContext.Provider value={{
      listings,
      sales,
      addListing,
      deleteListing,
      updateListing,
      setListing,
      addSale,
      undoSale,
      getSalesForListing,
      getTotalSalesRevenue,
      getActiveListingsCount,
      getSoldListingsCount
    }}>
      {children}
    </ListingContext.Provider>
  );
}
